<template>
  <div class="login-page">
    <div class="container-fluid h-100">
      <div class="row h-100">
        <!-- LEFT: Brand / Value Panel (NO IMAGE) -->
        <div
          class="col-lg-5 d-none d-lg-flex align-items-center justify-content-center login-left"
        >
          <div class="login-left-inner text-white">
            <!-- Brand -->
            <div class="d-flex align-items-center gap-3 mb-5">
              <hr-payroll-mark variant="full" tone="dark" />
            </div>

            <!-- Headline -->
            <h1 class="display-6 fw-bold mb-3">
              Sign in to run your <span class="text-white-75">people</span>,
              <span class="text-white-75">payroll</span> and
              <span class="text-white-75">operations</span> — faster.
            </h1>

            <p class="text-white-75 mb-4">
              Everything stays connected: HR actions feed payroll, attendance
              supports timesheets, deals become invoices, and projects track
              delivery. One source of truth for your team.
            </p>

            <!-- Trust chips -->
            <div class="trust-grid mb-5">
              <div class="trust-chip">
                <i class="ti ti-shield-check"></i>
                Secure & role-based access
              </div>
              <div class="trust-chip">
                <i class="ti ti-file-certificate"></i>
                Compliance-ready workflows
              </div>
              <div class="trust-chip">
                <i class="ti ti-report-analytics"></i>
                Real-time dashboards
              </div>
              <div class="trust-chip">
                <i class="ti ti-refresh"></i>
                Fewer tools, fewer errors
              </div>
            </div>

            <!-- Mini social proof -->
            <div class="proof-card">
              <div class="d-flex align-items-start gap-3">
                <div class="proof-icon">
                  <i class="ti ti-bolt"></i>
                </div>
                <div>
                  <div class="fw-bold mb-1">Built for fast-growing teams</div>
                  <div class="text-white-75 small">
                    Designed to keep your HR, payroll, finance and operations
                    aligned — without the busywork.
                  </div>
                  <div class="mt-3 d-flex flex-wrap gap-2">
                    <span class="pill">Company-scoped data</span>
                    <span class="pill">Approvals</span>
                    <span class="pill">Exports</span>
                    <span class="pill">Audit trails</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 text-white-50 small">
              <i class="ti ti-lock me-1"></i>
              Your data stays private. Your access stays controlled.
            </div>
          </div>
        </div>

        <!-- RIGHT: Login Form -->
        <div
          class="col-lg-7 col-md-12 d-flex align-items-center justify-content-center"
        >
          <div class="login-form-wrapper w-100">
            <transition name="fade" appear>
              <form class="login-card" @submit.prevent="onSubmit">
                <!-- Top -->
                <div
                  class="d-flex align-items-center justify-content-between mb-4"
                >
                  <a
                    v-if="useCentralMarketingLink"
                    :href="centralMarketingUrl('/')"
                    class="back-link"
                  >
                    <i class="ti ti-arrow-left"></i>
                    <span class="ms-2">Back to site</span>
                  </a>
                  <router-link v-else to="/" class="back-link">
                    <i class="ti ti-arrow-left"></i>
                    <span class="ms-2">Back to site</span>
                  </router-link>

                  <span class="badge-lite">
                    <i class="ti ti-shield-check me-1"></i>
                    Secure Sign-in
                  </span>
                </div>

                <!-- Header -->
                <div class="text-center mb-4">
                  <div class="brand-icon-sm mx-auto mb-3">
                    <i class="ti ti-login-2"></i>
                  </div>
                  <h2 class="fw-bold mb-1">Welcome back</h2>
                  <p class="text-muted mb-0">
                    Sign in to pick up where you left off — approvals, payroll,
                    CRM and reporting.
                  </p>
                </div>

                <!-- Email -->
                <div class="mb-3">
                  <label class="form-label">Email address</label>
                  <div class="input-group input-group-lg ux-input">
                    <span class="input-group-text">
                      <i class="ti ti-mail"></i>
                    </span>
                    <input
                      v-model="email"
                      type="email"
                      class="form-control"
                      placeholder="you@company.com"
                      autocomplete="email"
                    />
                  </div>
                  <small v-if="errors.email" class="text-danger d-block mt-1">
                    {{ errors.email }}
                  </small>
                </div>

                <!-- Password -->
                <div class="mb-2">
                  <label class="form-label">Password</label>
                  <div class="input-group input-group-lg ux-input">
                    <span class="input-group-text">
                      <i class="ti ti-key"></i>
                    </span>
                    <input
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      class="form-control"
                      placeholder="Enter your password"
                      autocomplete="current-password"
                    />
                    <button
                      type="button"
                      class="input-group-text btn-toggle"
                      @click="toggleShow"
                      :aria-label="
                        showPassword ? 'Hide password' : 'Show password'
                      "
                    >
                      <i
                        class="ti"
                        :class="showPassword ? 'ti-eye' : 'ti-eye-off'"
                      ></i>
                    </button>
                  </div>
                  <small
                    v-if="errors.password"
                    class="text-danger d-block mt-1"
                  >
                    {{ errors.password }}
                  </small>
                </div>

                <!-- Options -->
                <div
                  class="d-flex justify-content-between align-items-center mt-3 mb-4"
                >
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="remember"
                    />
                    <label class="form-check-label" for="remember">
                      Keep me signed in
                    </label>
                  </div>
                  <router-link
                    to="/forgot-password"
                    class="link-primary fw-semibold"
                  >
                    Forgot password?
                  </router-link>
                </div>

                <!-- Submit -->
                <button
                  type="submit"
                  class="btn btn-primary btn-lg w-100 btn-cta"
                  :disabled="loading"
                >
                  <span
                    v-if="loading"
                    class="d-inline-flex align-items-center justify-content-center"
                  >
                    <i class="ti ti-loader ti-spin me-2"></i>
                    Signing you in…
                  </span>
                  <span
                    v-else
                    class="d-inline-flex align-items-center justify-content-center"
                  >
                    Continue <i class="ti ti-arrow-right ms-2"></i>
                  </span>
                </button>

                <!-- Helper text -->
                <div class="hint mt-3">
                  <i class="ti ti-info-circle me-2"></i>
                  Tip: Use your company email to access the right modules and
                  dashboards.
                </div>

                <!-- Divider -->
                <div v-if="showCompanySignup" class="divider my-4">
                  <span>New to DoneQuick?</span>
                </div>

                <!-- Register -->
                <router-link
                  v-if="showCompanySignup"
                  to="/register"
                  class="btn btn-outline-dark btn-lg w-100"
                >
                  Create your company account
                </router-link>

                <!-- Footer -->
                <p class="text-center text-muted small mt-4 mb-0">
                  2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll
                </p>
              </form>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- subtle background blobs -->
    <div class="blob blob-1"></div>
    <div class="blob blob-2"></div>
  </div>
</template>

<script setup>
/* LOGIC UNCHANGED */
import { ref, onMounted, watch, computed } from "vue";
import HrPayrollMark from "@/components/branding/hr-payroll-mark.vue";
import { useRoute, useRouter } from "vue-router";
import { useLoginForm } from "@/composables/useLoginForm";
import { useAuthStore } from "@/stores/authStore";
import api from "@/services/apiService";
import { isTenantHost, isTenancyFrontendEnabled, centralMarketingUrl, isAdminHost } from "@/utils/hostname";

import { useForm, useField } from "vee-validate";
import * as yup from "yup";

const { submit, loading } = useLoginForm();

const schema = yup.object({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup.string().required("Password is required"),
});

const { handleSubmit, errors } = useForm({
  validationSchema: schema,
});

const { value: email } = useField("email");
const { value: password } = useField("password");

const showPassword = ref(false);
const toggleShow = () => (showPassword.value = !showPassword.value);

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const useCentralMarketingLink = computed(
  () => isTenancyFrontendEnabled() && isTenantHost()
);
const showCompanySignup = computed(
  () => !isAdminHost() && !isTenancyFrontendEnabled()
);
const updateBodyClass = () => {
  document.body.classList.add("bg-light");
};
onMounted(async () => {
  updateBodyClass();

  const impersonationToken = route.query.impersonate;
  if (
    impersonationToken &&
    isTenancyFrontendEnabled() &&
    isTenantHost()
  ) {
    try {
      const res = await api.post("impersonate/exchange", {
        token: impersonationToken,
      });
      if (res?.data?.token && res?.data?.user) {
        auth.setSession(res.data.token, res.data.user);
        await router.replace({ path: "/dashboard/admin-dashboard" });
      }
    } catch (e) {
      console.warn("Impersonation exchange failed", e?.message || e);
    }
  }
});
watch(() => route.fullPath, updateBodyClass);

const onSubmit = handleSubmit((values) => {
  submit(values);
});
</script>

<style scoped>
/* Base */
.login-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      900px 380px at 15% 0%,
      rgba(59, 130, 246, 0.16),
      transparent 60%
    ),
    radial-gradient(
      900px 380px at 85% 10%,
      rgba(99, 102, 241, 0.14),
      transparent 60%
    ),
    #f8fafc;
}

/* Background blobs */
.blob {
  position: absolute;
  filter: blur(45px);
  opacity: 0.35;
  pointer-events: none;
  z-index: 0;
}
.blob-1 {
  width: 420px;
  height: 420px;
  left: -160px;
  bottom: -160px;
  background: rgba(59, 130, 246, 0.55);
  border-radius: 999px;
}
.blob-2 {
  width: 520px;
  height: 520px;
  right: -220px;
  top: -220px;
  background: rgba(99, 102, 241, 0.55);
  border-radius: 999px;
}

/* ensure content above blobs */
.container-fluid,
.row {
  position: relative;
  z-index: 1;
}

/* Left panel */
.login-left {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  padding: 64px;
}
.login-left-inner {
  max-width: 520px;
}
.brand-icon {
  width: 56px;
  height: 56px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.78);
}

/* Trust chips */
.trust-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.trust-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  font-weight: 600;
}
.trust-chip i {
  color: rgba(34, 197, 94, 0.95);
}

/* Proof card */
.proof-card {
  margin-top: 18px;
  border-radius: 18px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
}
.proof-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.82rem;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.85);
}

/* Right panel */
.login-form-wrapper {
  max-width: 520px;
  padding: 24px;
}
.login-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(2, 6, 23, 0.06);
  padding: 34px;
  border-radius: 22px;
  box-shadow: 0 28px 70px rgba(2, 6, 23, 0.12);
  backdrop-filter: blur(10px);
}

/* Top actions */
.back-link {
  display: inline-flex;
  align-items: center;
  color: #334155;
  text-decoration: none;
  font-weight: 600;
}
.back-link:hover {
  text-decoration: underline;
}
.badge-lite {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.18);
  color: #166534;
  font-weight: 700;
  font-size: 0.84rem;
}

.brand-icon-sm {
  width: 52px;
  height: 52px;
  border-radius: 18px;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-size: 22px;
}

/* Inputs */
.ux-input .input-group-text {
  background: #fff;
  border-color: rgba(2, 6, 23, 0.12);
}
.ux-input .form-control {
  border-color: rgba(2, 6, 23, 0.12);
}
.ux-input:focus-within .input-group-text,
.ux-input:focus-within .form-control {
  border-color: rgba(37, 99, 235, 0.55);
  box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.12);
}

.btn-toggle {
  cursor: pointer;
}
.btn-toggle:hover {
  background: rgba(2, 6, 23, 0.03);
}

/* CTA button */
.btn-cta {
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(37, 99, 235, 0.25);
}
.btn-cta:disabled {
  box-shadow: none;
}

/* Hint */
.hint {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
  font-size: 0.92rem;
}

/* Divider */
.divider {
  position: relative;
  text-align: center;
}
.divider span {
  background: rgba(255, 255, 255, 0.92);
  padding: 0 12px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
}
.divider::before {
  content: "";
  position: absolute;
  inset: 50% 0 auto 0;
  height: 1px;
  background: rgba(2, 6, 23, 0.1);
  z-index: -1;
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.45s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 576px) {
  .login-card {
    padding: 26px;
    border-radius: 18px;
  }
}
</style>


